import axios from "axios";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PieController,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import { useEffect, useMemo, useState } from "react";
import { Bar, Line, Pie } from "react-chartjs-2";
import { FaPhoneAlt, FaUser } from "react-icons/fa";
import { PiChartLineUp } from "react-icons/pi";
import { TbPhoneCall } from "react-icons/tb";
import styles from "./SalesPersonDashboard.module.css";

// Register Chart.js components
ChartJS.register(
  LineElement,
  BarElement,
  PieController,
  ArcElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend
);

const SalesPersonDashboard = () => {
  const [campArr, setCampArr] = useState([]);
  const [agents, setAgents] = useState([]);

  const stats = [
    {
      label: "Calls Made Today",
      value: 23,
      icon: <FaPhoneAlt />,
    },
    {
      label: "Connected Calls",
      value: 12,
      icon: <TbPhoneCall />,
    },
    {
      label: "Campaigns Assigned",
      value: 2,
      icon: <PiChartLineUp />,
    },
    {
      label: "Total Leads",
      value: 70,
      icon: <FaUser />,
    },
  ];

  useEffect(() => {
    const getParticipantData = async () => {
      try {
        const token = localStorage.getItem("token");
        const userId = localStorage.getItem("userId");
        const response = await axios.get(
          `https://prod.toxboxai.com/api/get-participant-data/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        console.log("📥 Data received from backend:", response.data);
        setCampArr(response.data.campaignArray || []);
        setAgents(
          response.data.agentArray || [
            {
              name: "Luxury Flats Assistant",
              createdBy: "John Doe",
              campaign: "Luxury Villas – Pune",
              type: "Voice",
              createdOn: "Jul 20, 2025",
              assignedCount: 150,
              callsMade: 120,
              connectedCalls: 60,
              converted: 8,
              status: "ACTIVE",
            },
            {
              name: "Mumbai Commercial Bot",
              createdBy: "Jane Smith",
              campaign: "Commercial Spaces – Mumbai",
              type: "Chat",
              createdOn: "Jun 10, 2025",
              assignedCount: 80,
              callsMade: 70,
              connectedCalls: 35,
              converted: 5,
              status: "COMPLETED",
            },
            {
              name: "Delhi Homes AI",
              createdBy: "Alice Brown",
              campaign: "Affordable Homes – Delhi",
              type: "Hybrid",
              createdOn: "Aug 01, 2025",
              assignedCount: 200,
              callsMade: 180,
              connectedCalls: 90,
              converted: 12,
              status: "ACTIVE",
            },
            {
              name: "Bangalore Villa Agent",
              createdBy: "Bob White",
              campaign: "High-End Villas – Bangalore",
              type: "Voice",
              createdOn: "May 05, 2025",
              assignedCount: 100,
              callsMade: 80,
              connectedCalls: 40,
              converted: 6,
              status: "PAUSED",
            },
          ]
        );
      } catch (err) {
        console.error(
          "Error in getting the sales person details from frontend",
          err
        );
      }
    };

    getParticipantData();
  }, []);

  // Dynamic chart data based on agents
  const sortedAgents = useMemo(() => {
    return [...agents].sort(
      (a, b) => new Date(a.createdOn) - new Date(b.createdOn)
    );
  }, [agents]);

  const dailyCallsTrendData = useMemo(
    () => ({
      labels: sortedAgents.map((agent) => agent.createdOn),
      datasets: [
        {
          label: "Calls Made",
          data: sortedAgents.map((agent) => agent.callsMade),
          borderColor: "#4A90E2",
          backgroundColor: "rgba(74, 144, 226, 0.2)",
          fill: true,
          tension: 0.4,
        },
      ],
    }),
    [sortedAgents]
  );

  const leadConversionRateData = useMemo(
    () => ({
      labels: agents.map((agent) => agent.name),
      datasets: [
        {
          label: "Conversion Rate (%)",
          data: agents.map(
            (agent) => (agent.converted / agent.assignedCount) * 100
          ),
          backgroundColor: ["#50C878", "#FF6B6B", "#FFD700", "#6A5ACD"],
          borderColor: ["#3D9970", "#C13535", "#D4A017", "#483D8B"],
          borderWidth: 1,
        },
      ],
    }),
    [agents]
  );

  const callOutcomeDistributionData = useMemo(
    () => ({
      labels: ["Connected Calls", "Non-Connected Calls"],
      datasets: [
        {
          data: [
            agents.reduce((sum, agent) => sum + agent.connectedCalls, 0),
            agents.reduce(
              (sum, agent) => sum + (agent.callsMade - agent.connectedCalls),
              0
            ),
          ],
          backgroundColor: ["#36A2EB", "#FF6384"],
          borderColor: ["#2A80B9", "#CC4B5E"],
          borderWidth: 1,
        },
      ],
    }),
    [agents]
  );

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: {
          font: {
            size: 12,
          },
        },
      },
      title: {
        display: true,
        font: {
          size: 16,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Value",
          font: {
            size: 14,
          },
        },
      },
      x: {
        title: {
          display: true,
          text: "Category",
          font: {
            size: 14,
          },
        },
      },
    },
  };

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5; // you can change this number
  const assignments = campArr; // paginate campaigns
  const totalPages = Math.ceil(assignments.length / itemsPerPage);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Calculate assignments to display for current page
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentAssignments = assignments.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  // Generate page numbers for pagination
  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  return (
    <main className={styles.mainContainer}>
      {/* Performance Summary */}
      <section className={styles.summaryContainer}>
        <div className={styles.header}>
          <h2>Performance Summary</h2>
          <div className={styles.filterTabs}>
            <button className={`${styles.tab} ${styles.active}`}>Today</button>
            <button className={styles.tab}>This Week</button>
            <button className={styles.tab}>Custom</button>
          </div>
        </div>
        <div className={styles.cards}>
          {stats.map((item, index) => (
            <div key={index} className={styles.card}>
              <div className={styles.iconLabelContainer}>
                <div className={styles.icon}>{item.icon}</div>
                <div className={styles.label}>{item.label}</div>
              </div>
              <div className={styles.value}>{item.value}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Campaign Overview */}
      <section className={styles.container}>
        <h2 className={styles.title}>Campaign Overview</h2>
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>CAMPAIGN NAME</th>
                <th>PROPERTY TYPE</th>
                <th>LEADS ASSIGNED</th>
                <th>CREDIT BALANCE</th>
                <th>ASSIGNED ON</th>
                <th>STATUS</th>
              </tr>
            </thead>
            <tbody>
              {currentAssignments.map((item, index) => (
                <tr key={index}>
                  <td>{item.campaignname}</td>
                  <td>{item.type}</td>
                  <td>{item.sheetData?.length || 0}</td>
                  <td>{item.creditBalance}</td>
                  <td>{item.startDate}</td>
                  <td>
                    <span
                      className={`${styles.status} ${styles[item.campaignCurrentStatus?.toLowerCase()]
                        }`}
                    >
                      {item.campaignCurrentStatus}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <div className={styles.pagination}>
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={styles.pageButton}
        >
          Previous
        </button>
        {pageNumbers.map((page) => (
          <button
            key={page}
            onClick={() => handlePageChange(page)}
            className={`${styles.pageButton} ${currentPage === page ? styles.active : ""
              }`}
          >
            {page}
          </button>
        ))}
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={styles.pageButton}
        >
          Next
        </button>
      </div>

      {/* Agent Created Leads */}
      <section className={styles.agentsContainer}>
        <h2 className={styles.title}>Agent Created Leads</h2>
        <div className={styles.tableWrapper}>
          <table className={styles.agentsTable}>
            <thead>
              <tr>
                <th>AGENT NAME</th>
                <th>CREATED BY</th>
                <th>CAMPAIGN LINKED</th>
                <th>AGENT TYPE</th>
                <th>CREATED ON</th>
                <th>ASSIGNED LEAD COUNT</th>
                <th>CALLS MADE BY AGENT</th>
                <th>CONNECTED CALLS</th>
                <th>LEADS CONVERTED</th>
                <th>AGENT STATUS</th>
              </tr>
            </thead>
            <tbody>
              {agents.map((agent, index) => (
                <tr key={index}>
                  <td>{agent.name}</td>
                  <td>{agent.createdBy}</td>
                  <td>{agent.campaign}</td>
                  <td>{agent.type}</td>
                  <td>{agent.createdOn}</td>
                  <td>{agent.assignedCount}</td>
                  <td>{agent.callsMade}</td>
                  <td>{agent.connectedCalls}</td>
                  <td>{agent.converted}</td>
                  <td>
                    <span
                      className={`${styles.status} ${styles[agent.status.toLowerCase()]
                        }`}
                    >
                      {agent.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <div className={styles.pagination}>
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={styles.pageButton}
        >
          Previous
        </button>
        {pageNumbers.map((page) => (
          <button
            key={page}
            onClick={() => handlePageChange(page)}
            className={`${styles.pageButton} ${currentPage === page ? styles.active : ""
              }`}
          >
            {page}
          </button>
        ))}
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={styles.pageButton}
        >
          Next
        </button>
      </div>

      {/* Performance Charts & Insights */}
      <section>
        <h3 className={styles.metricHeading}>Performance Charts & Insights</h3>
        <div className={styles.chartsGrid}>
          <div className={styles.chartCard}>
            <div className={styles.chartTitle}>Daily Calls Trend</div>
            <div className={styles.chartWrapper}>
              <Line
                data={dailyCallsTrendData}
                options={{
                  ...chartOptions,
                  scales: {
                    y: {
                      beginAtZero: true,
                      title: { display: true, text: "Number of Calls" },
                    },
                    x: {
                      title: { display: true, text: "Date Created" },
                    },
                  },
                  plugins: {
                    ...chartOptions.plugins,
                    title: { display: true, text: "Daily Calls Trend" },
                  },
                }}
              />
            </div>
          </div>
          <div className={styles.chartCard}>
            <div className={styles.chartTitle}>Lead Conversion Rate</div>
            <div className={styles.chartWrapper}>
              <Bar
                data={leadConversionRateData}
                options={{
                  ...chartOptions,
                  scales: {
                    y: {
                      beginAtZero: true,
                      title: { display: true, text: "Conversion Rate (%)" },
                    },
                    x: {
                      title: { display: true, text: "Agent" },
                    },
                  },
                  plugins: {
                    ...chartOptions.plugins,
                    title: { display: true, text: "Lead Conversion Rate" },
                  },
                }}
              />
            </div>
          </div>
          <div className={styles.chartCard}>
            <div className={styles.chartTitle}>Call Outcome Distribution</div>
            <div className={styles.chartWrapper}>
              <Pie
                data={callOutcomeDistributionData}
                options={{
                  ...chartOptions,
                  scales: undefined,
                  plugins: {
                    ...chartOptions.plugins,
                    title: { display: true, text: "Call Outcome Distribution" },
                  },
                }}
              />
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default SalesPersonDashboard;
